import { Measurement, MeasurementType } from "@evident/ngx-media-viewer";
import { MEASUREMENT_MODE } from "../app.constants";
import {
  getMeasurementResults,
  UnitLabel,
  unitLabelOfUnitSystem,
  UnitSystem,
  unitSystemOfUnitLabel
} from "../inspection-data-management/image-edit-dialog/measurement-merry/measurement.type";

/** `type` of the measurement items ViSOL stores for the measurements of its Merry (media viewer) tab. */
export const MERRY_MEASUREMENT_ITEM_TYPE = "merry";
/** Version of the item shape below; bump it when the shape changes so readers can tell the versions apart. */
export const MERRY_MEASUREMENT_ITEM_VERSION = 1;

/**
 * One value of a Merry measurement in the shape of the legacy measurement tab's `measurementItems` (`{ type, data }`
 * with `data.id` = `<number>:<measureId>[:<pointId>]`, `data.mode`, `data.unit` and `data.result`): the shape every
 * report already reads (desktop/offline report, web report `_Column.cshtml`) and the sync carries. Values stay in
 * millimetres like the legacy ones; `unit` is the unit the user chose and the reports convert to it.
 */
export interface MerryMeasurementItem {
  type: typeof MERRY_MEASUREMENT_ITEM_TYPE;
  version: typeof MERRY_MEASUREMENT_ITEM_VERSION;
  data: MerryMeasurementItemData;
}

export interface MerryMeasurementItemData {
  id: string;
  mode: string;
  unit: UnitLabel;
  result: { length: number | null; area: number | null; objectDistance: null; latestDistance: null };
}

/** The legacy mode of each media viewer type (the reports translate the mode). */
const MODE_OF_TYPE: Partial<Record<MeasurementType, string>> = {
  [MeasurementType.DISTANCE]: MEASUREMENT_MODE.DISTANCE,
  [MeasurementType.POLYLINE]: MEASUREMENT_MODE.LINES,
  [MeasurementType.DEPTH]: MEASUREMENT_MODE.DEPTH,
  [MeasurementType.POINT_TO_LINE]: MEASUREMENT_MODE.POINT_TO_LINE,
  [MeasurementType.AREA]: MEASUREMENT_MODE.AREA,
  [MeasurementType.TIP_DEFECT]: MEASUREMENT_MODE.TIP_DEFECT
};

export class MerryMeasurementItems {
  /** The items to store for `measurements`, numbered in list order like the legacy tab numbers its results. */
  static toMeasurementItems(measurements: readonly Measurement[], unitSystem: UnitSystem): MerryMeasurementItem[] {
    const unit = unitLabelOfUnitSystem(unitSystem);
    return measurements.flatMap((measurement, index) => MerryMeasurementItems.toItems(measurement, index + 1, unit));
  }

  /**
   * The unit the stored items were saved with (both measurement tabs store it on every item), undefined when there is
   * none: no items, or items without a unit.
   */
  static unitSystemOf(measurementItems: unknown): UnitSystem | undefined {
    if (!Array.isArray(measurementItems)) {
      return undefined;
    }
    const unit = measurementItems
      .map((item) => (item as { data?: { unit?: unknown } } | null)?.data?.unit)
      .find((value) => typeof value === "string");
    return typeof unit === "string" ? unitSystemOfUnitLabel(unit) : undefined;
  }

  private static toItems(measurement: Measurement, number: number, unit: UnitLabel): MerryMeasurementItem[] {
    const mode = MODE_OF_TYPE[measurement.type];
    if (!mode) {
      return [];
    }
    const results = getMeasurementResults(measurement);

    if (measurement.hasSubPoints()) {
      // One value per placed point (Depth, PointToLine): one item each, under the same number.
      return results.map((result) => toItem(`${number}:${measurement.measureId}:${result.pointId}`, mode, unit, { length: result.value }));
    }

    // The first value is the one the reports show: the length, or the area (Area, and TipDefect before its edges).
    const primary = results[0];
    if (!primary) {
      return [];
    }
    const result = primary.unitType === "area" ? { area: primary.value } : { length: primary.value };
    return [toItem(`${number}:${measurement.measureId}`, mode, unit, result)];
  }
}

const toItem = (id: string, mode: string, unit: UnitLabel, result: { length?: number; area?: number }): MerryMeasurementItem => ({
  type: MERRY_MEASUREMENT_ITEM_TYPE,
  version: MERRY_MEASUREMENT_ITEM_VERSION,
  data: {
    id,
    mode,
    unit,
    result: { length: result.length ?? null, area: result.area ?? null, objectDistance: null, latestDistance: null }
  }
});
